import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Button
} from '@mui/material';
import { Download, Payment, CreditCard } from '@mui/icons-material';
import { API_BASE_URL, authFetch } from '../../config/api.config';

interface Invoice {
  id: number;
  invoiceNumber: string;
  bookingNumber: string;
  bookingId?: number;
  invoiceDate: string;
  dueDate: string;
  // Backend uses these field names
  totalAmount?: number;
  subTotal?: number;
  amountDue?: number;
  amountPaid?: number;
  // Legacy field names for compatibility
  totalGross?: number;
  totalNet?: number;
  remainingAmount?: number;
  paidAmount?: number;
  // Status fields
  status: string;
  isPaid?: boolean;
  isFullyPaid?: boolean;
  paidDate?: string;
  // Voucher discount from booking (added when loading)
  voucherDiscount?: number;
}

interface Booking {
  id: number;
  bookingNumber: string;
  scheduleDate: string;
  tourTypeName: string;
  status: string;
  pricing?: {
    totalGross: number;
    depositAmount: number;
  };
  totalPrice?: number;
  totalAmount?: number; // Some APIs use this
  voucherDiscount?: number; // Amount redeemed from voucher
  finalAmount?: number; // Final amount after voucher discount
  paymentStatus?: string;
  paidAmount?: number;
  amountPaid?: number; // Some APIs use this
  payments?: PaymentRecord[];
}

interface PaymentRecord {
  id: number;
  amount: number;
  paymentDate: string;
  paymentMethod: string;
  status: string;
  transactionId?: string;
}

interface VoucherInvoice {
  id: number;
  invoiceNumber: string;
  voucherCode: string;
  voucherId: number;
  invoiceDate: string;
  amount: number;
  paymentStatus: string;
}

const PaymentHistory: React.FC = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [voucherInvoices, setVoucherInvoices] = useState<VoucherInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadPaymentData();
  }, []);

  const loadPaymentData = async () => {
    try {
      setLoading(true);
      setError(null);
      // T-009: authFetch setzt die Auth-Header und refresht bei 401 automatisch
      // Load bookings with payment info - use user/bookings endpoint
      const bookingsResponse = await authFetch(`${API_BASE_URL}/user/bookings`, {});

      if (bookingsResponse.ok) {
        const bookingsData = await bookingsResponse.json();
        // API returns { items: [...], totalCount: X }
        let bookingsList: Booking[] = [];
        if (Array.isArray(bookingsData)) {
          bookingsList = bookingsData;
        } else if (bookingsData.items && Array.isArray(bookingsData.items)) {
          bookingsList = bookingsData.items;
        } else if (bookingsData.bookings && Array.isArray(bookingsData.bookings)) {
          bookingsList = bookingsData.bookings;
        } else if (bookingsData.data && Array.isArray(bookingsData.data)) {
          bookingsList = bookingsData.data;
        }

        setBookings(bookingsList);

        // Load invoices for each booking
        const allInvoices: Invoice[] = [];
        for (const booking of bookingsList) {
          try {
            const invoiceResponse = await authFetch(`${API_BASE_URL}/invoices/booking/${booking.id}`, {});
            if (invoiceResponse.ok) {
              const invoiceData = await invoiceResponse.json();
              if (Array.isArray(invoiceData)) {
                allInvoices.push(...invoiceData.map((inv: any) => ({
                  ...inv,
                  bookingNumber: booking.bookingNumber,
                  bookingId: booking.id,
                  // Pass voucher discount from booking to invoice for display
                  voucherDiscount: booking.voucherDiscount || 0
                })));
              }
            }
          } catch {
            // Invoice not found for this booking, skip
          }
        }
        setInvoices(allInvoices);

        // Load voucher invoices
        try {
          const voucherInvoicesResponse = await authFetch(`${API_BASE_URL}/vouchers/my-invoices`, {});
          if (voucherInvoicesResponse.ok) {
            const voucherInvoicesData = await voucherInvoicesResponse.json();
            setVoucherInvoices(voucherInvoicesData);
          }
        } catch {
          console.error('Error loading voucher invoices');
        }
      } else {
        throw new Error('Fehler beim Laden der Buchungen');
      }
    } catch (err) {
      console.error('Error loading payment data:', err);
      setError('Fehler beim Laden der Zahlungsdaten');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const getStatusChip = (status: string, isPaid: boolean) => {
    if (isPaid) {
      return <Chip label="Bezahlt" color="success" size="small" />;
    }
    switch (status?.toLowerCase()) {
      case 'paid':
      case 'bezahlt':
        return <Chip label="Bezahlt" color="success" size="small" />;
      case 'pending':
      case 'ausstehend':
        return <Chip label="Ausstehend" color="warning" size="small" />;
      case 'overdue':
      case 'überfällig':
        return <Chip label="Überfällig" color="error" size="small" />;
      case 'cancelled':
      case 'storniert':
        return <Chip label="Storniert" color="default" size="small" />;
      default:
        return <Chip label={status || 'Offen'} color="info" size="small" />;
    }
  };

  const getPaymentStatusChip = (booking: Booking) => {
    // Check payment status from booking
    if (booking.paymentStatus === 'FullyPaid' || booking.paymentStatus === 'Paid') {
      return <Chip label="Vollständig bezahlt" color="success" size="small" />;
    }
    if (booking.paymentStatus === 'PartiallyPaid' || booking.paymentStatus === 'DepositPaid') {
      return <Chip label="Anzahlung bezahlt" color="info" size="small" />;
    }
    if (booking.paymentStatus === 'Pending') {
      return <Chip label="Zahlung ausstehend" color="warning" size="small" />;
    }

    return <Chip label="Offen" color="default" size="small" />;
  };

  const isPaymentPending = (booking: Booking): boolean => {
    const status = booking.paymentStatus;
    return !status || status === 'Pending' || status === 'PartiallyPaid' || status === 'DepositPaid' || status === 'Open';
  };

  const getTotalAmount = (booking: Booking): number => {
    // Use finalAmount if available (already has voucher discount applied)
    // Otherwise fall back to other total amount fields
    if (booking.finalAmount !== undefined && booking.finalAmount !== null) {
      return booking.finalAmount;
    }
    // Calculate final amount manually if voucherDiscount is present
    const subtotal = booking.totalPrice || booking.totalAmount || booking.pricing?.totalGross || 0;
    const voucherDiscount = booking.voucherDiscount || 0;
    return Math.max(0, subtotal - voucherDiscount);
  };

  const getPaidAmount = (booking: Booking): number => {
    // Try all possible field names for paid amount
    return booking.paidAmount || booking.amountPaid || 0;
  };

  const getOpenAmount = (booking: Booking): number => {
    const totalPrice = getTotalAmount(booking);
    const paidAmount = getPaidAmount(booking);
    return totalPrice - paidAmount;
  };

  const handlePayNow = (bookingId: number) => {
    // Navigate to checkout/payment page for this booking
    navigate(`/checkout/${bookingId}`);
  };

  const handleDownloadBookingInvoice = async (bookingId: number, bookingNumber: string) => {
    try {
      // First try to get invoices for this booking
      const invoiceResponse = await authFetch(`${API_BASE_URL}/invoices/booking/${bookingId}`, {});

      if (invoiceResponse.ok) {
        const invoiceData = await invoiceResponse.json();
        if (Array.isArray(invoiceData) && invoiceData.length > 0) {
          // Download the most recent invoice
          const invoice = invoiceData[0];
          await handleDownloadInvoice(invoice.id, invoice.invoiceNumber || bookingNumber);
          return;
        }
      }

      // If no invoice found, try alternative endpoint
      const response = await authFetch(`${API_BASE_URL}/user/bookings/${bookingId}/invoice`, {});

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Rechnung-${bookingNumber}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('Für diese Buchung ist noch keine Rechnung verfügbar.');
      }
    } catch (error) {
      console.error('Error downloading invoice:', error);
      alert('Fehler beim Herunterladen der Rechnung.');
    }
  };

  const handleDownloadInvoice = async (invoiceId: number, invoiceNumber: string) => {
    try {
      const response = await authFetch(`${API_BASE_URL}/invoices/${invoiceId}/pdf`, {});

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${invoiceNumber}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('Rechnung konnte nicht heruntergeladen werden.');
      }
    } catch (error) {
      console.error('Error downloading invoice:', error);
      alert('Fehler beim Herunterladen der Rechnung.');
    }
  };

  const handlePayInvoice = (invoice: Invoice) => {
    // Navigate to checkout with the invoice amount
    if (invoice.bookingId) {
      navigate(`/checkout/${invoice.bookingId}`);
    }
  };

  const handleDownloadVoucherInvoice = async (voucherId: number, invoiceNumber: string) => {
    try {
      const response = await authFetch(`${API_BASE_URL}/vouchers/${voucherId}/invoice/pdf`, {});

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${invoiceNumber}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('Rechnung konnte nicht heruntergeladen werden.');
      }
    } catch (error) {
      console.error('Error downloading voucher invoice:', error);
      alert('Fehler beim Herunterladen der Rechnung.');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  const hasData = bookings.length > 0 || invoices.length > 0 || voucherInvoices.length > 0;

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Zahlungsübersicht
      </Typography>

      {!hasData ? (
        <Paper elevation={1} sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            Noch keine Zahlungsdaten vorhanden.
          </Typography>
        </Paper>
      ) : (
        <>
          {/* Bookings with Payment Status */}
          <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
            Buchungen
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 4 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell><strong>Buchungsnummer</strong></TableCell>
                  <TableCell><strong>Datum</strong></TableCell>
                  <TableCell><strong>Tour</strong></TableCell>
                  <TableCell align="right"><strong>Gesamtbetrag</strong></TableCell>
                  <TableCell align="right"><strong>Offen</strong></TableCell>
                  <TableCell align="center"><strong>Status</strong></TableCell>
                  <TableCell align="center"><strong>Aktionen</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bookings.map((booking) => {
                  const totalPrice = getTotalAmount(booking);
                  const openAmount = getOpenAmount(booking);
                  const hasPendingPayment = isPaymentPending(booking) && openAmount > 0;

                  return (
                    <TableRow key={booking.id} hover>
                      <TableCell>{booking.bookingNumber}</TableCell>
                      <TableCell>{formatDate(booking.scheduleDate)}</TableCell>
                      <TableCell>{booking.tourTypeName || '-'}</TableCell>
                      <TableCell align="right">{formatCurrency(totalPrice)}</TableCell>
                      <TableCell align="right" sx={{ color: openAmount > 0 ? '#d32f2f' : '#2e7d32', fontWeight: 500 }}>
                        {formatCurrency(openAmount)}
                      </TableCell>
                      <TableCell align="center">{getPaymentStatusChip(booking)}</TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          {hasPendingPayment && (
                            <Tooltip title="Jetzt online bezahlen">
                              <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                startIcon={<CreditCard />}
                                onClick={() => handlePayNow(booking.id)}
                                sx={{
                                  fontSize: '0.75rem',
                                  textTransform: 'none',
                                  minWidth: 'auto'
                                }}
                              >
                                Bezahlen
                              </Button>
                            </Tooltip>
                          )}
                          <Tooltip title="Rechnung herunterladen">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleDownloadBookingInvoice(booking.id, booking.bookingNumber)}
                            >
                              <Download />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Invoices */}
          {invoices.length > 0 && (
            <>
              <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
                Rechnungen
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell><strong>Rechnungsnummer</strong></TableCell>
                      <TableCell><strong>Buchung</strong></TableCell>
                      <TableCell><strong>Datum</strong></TableCell>
                      <TableCell><strong>Fällig am</strong></TableCell>
                      <TableCell align="right"><strong>Betrag</strong></TableCell>
                      <TableCell align="right"><strong>Offen</strong></TableCell>
                      <TableCell align="center"><strong>Status</strong></TableCell>
                      <TableCell align="center"><strong>Aktionen</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoices.map((invoice) => {
                      // Get raw total amount - try multiple field names
                      const rawTotalAmount = invoice.totalAmount ?? invoice.totalGross ?? 0;
                      // Get voucher discount from booking
                      const voucherDiscount = invoice.voucherDiscount ?? 0;
                      // Apply voucher discount to total amount
                      const totalAmount = Math.max(0, rawTotalAmount - voucherDiscount);
                      // Get amount paid
                      const paidAmount = invoice.amountPaid ?? invoice.paidAmount ?? 0;
                      // Get remaining/open amount - prefer amountDue (with voucher adjustment), calculate as fallback
                      const isPaid = invoice.isFullyPaid ?? invoice.isPaid ?? false;
                      const rawRemainingAmount = invoice.amountDue ?? invoice.remainingAmount ?? (isPaid ? 0 : rawTotalAmount - paidAmount);
                      // Apply voucher discount to remaining amount as well
                      const remainingAmount = Math.max(0, rawRemainingAmount - voucherDiscount);
                      const canPay = !isPaid && remainingAmount > 0;

                      return (
                        <TableRow key={invoice.id} hover>
                          <TableCell>{invoice.invoiceNumber}</TableCell>
                          <TableCell>{invoice.bookingNumber}</TableCell>
                          <TableCell>{formatDate(invoice.invoiceDate)}</TableCell>
                          <TableCell>{formatDate(invoice.dueDate)}</TableCell>
                          <TableCell align="right">{formatCurrency(totalAmount)}</TableCell>
                          <TableCell align="right" sx={{ color: remainingAmount > 0 ? '#d32f2f' : '#2e7d32', fontWeight: 500 }}>
                            {formatCurrency(remainingAmount)}
                          </TableCell>
                          <TableCell align="center">{getStatusChip(invoice.status, isPaid)}</TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                              {canPay && (
                                <Tooltip title="Jetzt online bezahlen">
                                  <Button
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    startIcon={<CreditCard />}
                                    onClick={() => handlePayInvoice(invoice)}
                                    sx={{
                                      fontSize: '0.75rem',
                                      textTransform: 'none',
                                      minWidth: 'auto'
                                    }}
                                  >
                                    Bezahlen
                                  </Button>
                                </Tooltip>
                              )}
                              <Tooltip title="PDF herunterladen">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleDownloadInvoice(invoice.id, invoice.invoiceNumber)}
                                >
                                  <Download />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {/* Gutschein-Rechnungen - nur unbezahlte anzeigen */}
          {voucherInvoices.filter(inv => inv.paymentStatus !== 'Paid').length > 0 && (
            <>
              <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
                Gutschein-Rechnungen (ausstehend)
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell><strong>Rechnungsnummer</strong></TableCell>
                      <TableCell><strong>Gutschein</strong></TableCell>
                      <TableCell><strong>Datum</strong></TableCell>
                      <TableCell align="right"><strong>Betrag</strong></TableCell>
                      <TableCell align="center"><strong>Status</strong></TableCell>
                      <TableCell align="center"><strong>Aktionen</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {voucherInvoices
                      .filter(invoice => invoice.paymentStatus !== 'Paid')
                      .map((invoice) => (
                        <TableRow key={invoice.id} hover>
                          <TableCell>{invoice.invoiceNumber}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', color: '#910F0F' }}>
                            {invoice.voucherCode}
                          </TableCell>
                          <TableCell>{formatDate(invoice.invoiceDate)}</TableCell>
                          <TableCell align="right">{formatCurrency(invoice.amount)}</TableCell>
                          <TableCell align="center">
                            <Chip
                              label="Zahlung ausstehend"
                              color="warning"
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                              <Tooltip title="Überweisungsdaten anzeigen">
                                <Button
                                  variant="contained"
                                  color="primary"
                                  size="small"
                                  startIcon={<CreditCard />}
                                  onClick={() => navigate(`/gutschein-kaufen/vorkasse?code=${invoice.voucherCode}&amount=${invoice.amount}&voucherId=${invoice.voucherId}`)}
                                  sx={{
                                    fontSize: '0.75rem',
                                    textTransform: 'none',
                                    minWidth: 'auto'
                                  }}
                                >
                                  Bezahlen
                                </Button>
                              </Tooltip>
                              <Tooltip title="PDF herunterladen">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleDownloadVoucherInvoice(invoice.voucherId, invoice.invoiceNumber)}
                                >
                                  <Download />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default PaymentHistory;
